"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useRouter, useParams } from 'next/navigation';
import CandidateCard, { CandidateProps } from './CandidateCard';
import styles from './CandidateLayout.module.css';

function CustomDropdown({ options, value, onChange, placeholder }: { options: string[], value: string, onChange: (val: string) => void, placeholder: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className={styles.customDropdown}>
      <div className={styles.customDropdownHeader} onClick={() => setIsOpen(!isOpen)}>
        <span>{value || placeholder}</span>
        <span className={styles.customDropdownArrow}>▼</span>
      </div>
      {isOpen && (
        <ul className={styles.customDropdownList}>
          <li 
            className={`${styles.customDropdownItem} ${!value ? styles.activeItem : ''}`}
            onClick={() => { onChange(''); setIsOpen(false); }}
          >
            {placeholder}
          </li>
          {options.map(opt => (
            <li 
              key={opt}
              className={`${styles.customDropdownItem} ${value === opt ? styles.activeItem : ''}`}
              onClick={() => { onChange(opt); setIsOpen(false); }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CandidateLayoutProps {
  initialCandidates: CandidateProps[];
}

export default function CandidateLayout({ initialCandidates }: CandidateLayoutProps) {
  const router = useRouter();
  const params = useParams();
  const lang = (params?.lang as string) || 'sq';
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    if (role === 'administrator') {
      setIsAuthorized(true);
    } else {
      router.replace(`/${lang}`);
    }
  }, [lang, router]);

  const [candidates, setCandidates] = useState<CandidateProps[]>(initialCandidates);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [sortOption, setSortOption] = useState('New');

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const [isNameExpanded, setIsNameExpanded] = useState(true);
  const [isSkillExpanded, setIsSkillExpanded] = useState(true);
  const [isLocationExpanded, setIsLocationExpanded] = useState(true);
  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);

  // Extract unique filters for dropdowns
  const allSkills = Array.from(new Set(initialCandidates.flatMap(c => c.skills)));
  const allLocations = Array.from(new Set(initialCandidates.map(c => c.location).filter(Boolean)));
  const allLanguages = Array.from(new Set(initialCandidates.flatMap(c => c.languages || [])));

  useEffect(() => {
    let filtered = initialCandidates;
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (c.profession || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedSkill) {
      filtered = filtered.filter(c => c.skills.includes(selectedSkill));
    }
    if (selectedLocation) {
      filtered = filtered.filter(c => c.location === selectedLocation);
    }
    if (selectedLanguage) {
      filtered = filtered.filter(c => c.languages?.includes(selectedLanguage));
    }

    if (sortOption === 'New') {
      // Assuming initial order or IDs represent newest, or implement real date sorting if data has dates
      // Default to keeping the order or reversing if it's oldest first
    } else if (sortOption === 'Oldest') {
      filtered = [...filtered].reverse();
    } else if (sortOption === 'A-Z') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOption === 'Z-A') {
      filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
    }

    setCandidates(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedSkill, selectedLocation, selectedLanguage, sortOption, initialCandidates]);

  const totalPages = Math.ceil(candidates.length / ITEMS_PER_PAGE);
  const paginatedCandidates = candidates.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className={styles.container}>
      <aside className={styles.sidebar}>
        <h2 className={styles.filterTitle}>Filter By</h2>
        
        <div className={styles.filterContainer}>
          <div className={styles.filterGroup}>
            <div className={styles.filterHeader} onClick={() => setIsNameExpanded(!isNameExpanded)} style={{ cursor: 'pointer' }}>
              <label style={{ cursor: 'pointer' }}>Name or Keyword</label>
              <span className={styles.iconCircle}>
                {isNameExpanded ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                )}
              </span>
            </div>
            {isNameExpanded && (
              <div className={styles.inputWrapper}>
                <input 
                  type="text" 
                  placeholder="Name or keyword" 
                  className={styles.input}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </div>
            )}
          </div>

          <div className={styles.filterDivider}></div>

          <div className={styles.filterGroup}>
            <div className={styles.filterHeader} onClick={() => setIsSkillExpanded(!isSkillExpanded)} style={{ cursor: 'pointer' }}>
              <label style={{ cursor: 'pointer' }}>Skill</label>
              <span className={styles.iconCircle}>
                {isSkillExpanded ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                )}
              </span>
            </div>
            {isSkillExpanded && (
              <CustomDropdown 
                options={allSkills} 
                value={selectedSkill} 
                onChange={setSelectedSkill} 
                placeholder="All Skills" 
              />
            )}
          </div>

          <div className={styles.filterDivider}></div>

          <div className={styles.filterGroup}>
            <div className={styles.filterHeader} onClick={() => setIsLocationExpanded(!isLocationExpanded)} style={{ cursor: 'pointer' }}>
              <label style={{ cursor: 'pointer' }}>Location</label>
              <span className={styles.iconCircle}>
                {isLocationExpanded ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                )}
              </span>
            </div>
            {isLocationExpanded && (
              <CustomDropdown 
                options={allLocations} 
                value={selectedLocation} 
                onChange={setSelectedLocation} 
                placeholder="All Locations" 
              />
            )}
          </div>

          <div className={styles.filterDivider}></div>

          <div className={styles.filterGroup}>
            <div className={styles.filterHeader} onClick={() => setIsLanguageExpanded(!isLanguageExpanded)} style={{ cursor: 'pointer' }}>
              <label style={{ cursor: 'pointer' }}>Language</label>
              <span className={styles.iconCircle}>
                {isLanguageExpanded ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                )}
              </span>
            </div>
            {isLanguageExpanded && (
              <CustomDropdown 
                options={allLanguages} 
                value={selectedLanguage} 
                onChange={setSelectedLanguage} 
                placeholder="All Languages" 
              />
            )}
          </div>
        </div>
      </aside>

      <main className={styles.main}>
        <header className={styles.header}>
          <div className={styles.resultsCount}>
            All <strong>{candidates.length}</strong> candidates found
          </div>
          <div className={styles.sortOptions}>
            <span>Sort:</span>
            <select className={styles.sortSelect} value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
              <option value="New">New</option>
              <option value="Oldest">Oldest</option>
              <option value="A-Z">A-Z</option>
              <option value="Z-A">Z-A</option>
            </select>
          </div>
        </header>

        <div className={styles.grid}>
          {paginatedCandidates.map(candidate => (
            <CandidateCard key={candidate.id} candidate={candidate} />
          ))}
        </div>

        {totalPages > 0 && (
          <div className={styles.pagination}>
            <div className={styles.pageNumbers}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button 
                  key={page} 
                  className={currentPage === page ? styles.pageBtnActive : styles.pageBtn}
                  onClick={() => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button 
                  className={styles.pageBtn}
                  onClick={() => {
                    setCurrentPage(prev => prev + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  &gt;
                </button>
              )}
            </div>
            <div className={styles.showingText}>
              Showing <strong>{(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, candidates.length)}</strong> of <strong>{candidates.length}</strong>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
